// Description: This component is responsible for rendering the login form and handling the login process.
// Updated: 04/08/2025
// Author: Vitor Jesus


// React
import React, { useState } from "react";
import { useLocation } from "react-router-dom";

// Material UI Components
import { Box, useMediaQuery } from "@mui/material";

// Components and Functions for the project
import { AuthLogin } from "../../api/aws/Auth/AuthLogin";
import FormLogin from "./FormLogin";
import Logo from "../../assets/ilustrations/Logo";
import "./styles.css";
import MobileLoginPage from "./MobileLoginPage";

// Render The Component
const LoginForm = () => {
  // State Variables
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const location = useLocation();
  const messageParams = location.state ? location.state.message : "";
  const [message, setMessage] = useState(messageParams ? messageParams : "");

  const [checked, setChecked] = React.useState(false);
  const FRONTURL_BASED = process.env.REACT_APP_FRONTEND_URL;

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleNotify = (returnMessage) => {

    if (returnMessage.includes("Field") && returnMessage.includes("is required")) {
      setMessage("Preencha todos os campos obrigatórios.");
      return;
    }

    const messages = {
      "Invalid credentials": "Credenciais inválidas.",
      "You need to provide your password.": "Você precisa fornecer sua senha.",
      "You need to complete the SRP (Secure Remote Password) challenge.": "Você precisa completar o desafio SRP (Senha Remota Segura).",
      "You need to authenticate using WebAuthn.": "Você precisa se autenticar usando WebAuthn.",
      "Please provide the SMS OTP code sent to your phone.": "Insira o código SMS enviado para seu celular.",
      "Please provide the email OTP code sent to your email.": "Insira o código enviado para seu e-mail.",
      "Please provide the MFA code from your authenticator app.": "Insira o código MFA do seu aplicativo autenticador.",
      "You need to answer a custom challenge.": "Você precisa responder a um desafio personalizado.",
      "Please provide a new password.": "Você precisa fornecer uma nova senha.",
      "Set up MFA (Multi-Factor Authentication).": "Configure a autenticação de dois fatores (MFA).",
      "Please select your MFA type.": "Selecione o tipo de MFA desejado.",
      "An unexpected error occurred": "Ocorreu um erro inesperado. Tente novamente mais tarde."
    };

    if (messages[returnMessage]) {
      setMessage(messages[returnMessage]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!username || !password) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);

    try {
      const response = await AuthLogin({ username, password });

      if ((response.status || response.message) === "success" && response.accessToken) {

        // Limpa cookies indesejados
        document.cookie.split(";").forEach((cookie) => {
          const cookieName = cookie.split("=")[0].trim();
          if (!["_chatwoot_session", "cw_conversation"].includes(cookieName)) {
            document.cookie = `${cookieName}=;expires=${new Date().toUTCString()};path=/; SameSite=None; Secure`;
          }
        });

        // Armazena o token no localStorage
        const expirationTime = Date.now() + 30 * 60 * 1000;
        localStorage.setItem("expiration_time", expirationTime);
        localStorage.setItem("jwt_token", response.accessToken);
        localStorage.setItem("idToken", response.idToken);
        localStorage.setItem("refreshToken", response.refreshToken);

        window.location.href = "/";

      } else {
        handleNotify(response.message || "Erro inesperado. Tente novamente mais tarde.");
      }
    } catch (error) {
      handleNotify(error.message)
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isSmallScreen ? (
        <MobileLoginPage
          checked={checked}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          loading={loading}
          message={message}
          password={password}
          setPassword={setPassword}
          setUsername={setUsername}
          username={username}
        />
      ) : (
        <>
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              padding: "10px",
            }}
          >
            <Logo />
          </div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: { xs: "start", lg: "center" },
              height: "100vh",
              background: 'linear-gradient(135deg, #ffffff 0%, #EDE8FF 100%)',
            }}
          >
            <FormLogin
              username={username}
              setUsername={setUsername}
              password={password}
              setPassword={setPassword}
              message={message}
              loading={loading}
              checked={checked}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default LoginForm;
